import React, { useCallback, useEffect, useState } from 'react'
import {
  Box,
  Divider,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useMutation } from '@apollo/client'
import { useTranslation } from '../../../common/hooks/helper/useTranslation'
import { useUserProfile } from '../../../common/hooks/useUserProfile'

import { UPDATE_PATIENT_PROFILE } from '../../../operations/patientProfileOperations'
import {
  getPatientProfile,
  updatePatientProfile,
  updatePatientProfileVariables,
  EditablePatientData,
} from '../../../models/graphqlTypes'
import { ValidPhonePattern } from '../../../utils/isValidPhone'
import { ScrollablePanel } from '../../../common/components/ScrollablePanel'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { SaveButton } from '../../../common/components/SaveButton'
import { PasswordReset } from '../../../common/components/PasswordReset'
import { prettifyBloodType } from '../../../utils/bloodType'
import { BoxWithLabel } from '../../../common/components/BoxWithLabel'
import { useStoreActions } from '../../../store/store.hooks'
import { useCitiesByPostalCode } from '../../../common/hooks/useCitiesByPostalCode'
import { MeasurementFieldController } from '../../../common/components'
import { usePatientProfile } from '../../../common/hooks/usePatientProfile'

const FIRST_COL_WIDTH = 150

const PatientProfile: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { t } = useTranslation()
  const setToast = useStoreActions((actions) => actions.toast.setToast)

  const [cityOptions, setCityOptions] = useState<string[]>([''])

  const formMethods = useForm<EditablePatientData>({
    mode: 'all',
    defaultValues: { addressCity: '' },
  })

  const {
    watch,
    register,
    reset,
    formState: { errors },
    handleSubmit,
    trigger,
    control,
    setValue,
  } = formMethods

  const watchAllFields = watch()

  const resetFormWithPatientProfile = (
    patientProfile: getPatientProfile['getPatientProfile']
  ) => {
    reset({
      birthPlace: patientProfile.birthPlace,
      motherFirstName: patientProfile.motherFirstName,
      motherLastName: patientProfile.motherLastName,
      gender: patientProfile.gender,
      title: patientProfile.title,
      phone: patientProfile.phone,
      addressCity: patientProfile.addressCity ?? '',
      addressPostalCode: patientProfile.addressPostalCode,
      addressStreet: patientProfile.addressStreet,
      height: patientProfile.height,
      weight: patientProfile.weight,
      takenMedicines: patientProfile.takenMedicines,
      medicineIntolerance: patientProfile.medicineIntolerance,
    })
  }

  const profile = useUserProfile()
  const patientId = profile?.id

  const {
    data: patientProfile,
    refetch: patientProfileRefetch,
    loading: isLoadingPatientProfile,
  } = usePatientProfile({
    variables: { patientId: patientId! },
    onCompleted: (data) => {
      if (data.getPatientProfile) {
        resetFormWithPatientProfile(data.getPatientProfile)
      }
    },
  })

  const [updatePatientProfileMutation, { loading: isUpdatingProfile }] =
    useMutation<updatePatientProfile, updatePatientProfileVariables>(
      UPDATE_PATIENT_PROFILE,
      {
        onCompleted: () => {
          patientProfileRefetch()
          reset(watchAllFields)
          setToast({
            text: t('notification:surveySuccesfullySent'),
            type: 'success',
          })
        },
      }
    )

  const { refetch: refetchCities, loading: isCitiesLoading } =
    useCitiesByPostalCode({
      variables: { postalCode: patientProfile?.addressPostalCode || '' },
      onCompleted: (data) => {
        const cities = data.getLocationByPostalCode ?? []
        setCityOptions(cities)
      },
    })

  useEffect(() => {
    const { unsubscribe } = watch(async (value, { name }) => {
      if (
        name === 'addressPostalCode' &&
        value.addressPostalCode?.length === 4
      ) {
        const { data } = await refetchCities({
          postalCode: value.addressPostalCode,
        })

        const cities = data.getLocationByPostalCode
        setCityOptions(cities)
        setValue('addressCity', cities[0] || '')
      }
    })
    return () => unsubscribe()
  }, [refetchCities, setValue, watch])

  const submitForm = useCallback(
    (formFields: EditablePatientData) => {
      const height = formFields.height as number
      const weight = formFields.weight as number

      if (!patientId) {
        return
      }

      updatePatientProfileMutation({
        variables: {
          patientId,
          updatePatientInput: {
            ...formFields,
            height: isNaN(height) ? undefined : +height,
            weight: isNaN(weight) ? undefined : +weight,
          },
        },
      })
    },
    [patientId, updatePatientProfileMutation]
  )

  const required = {
    value: true,
    message: t('messages:warnings.required'),
  }

  return (
    <ScrollablePanel
      title={t('patients:personalData')}
      headerRightContent={
        <Box display="flex" alignItems="center">
          <Box mr={2}>
            <PasswordReset />
          </Box>
          <Box>
            <SaveButton
              isSaving={isUpdatingProfile}
              onClick={handleSubmit(submitForm)}
            />
          </Box>
        </Box>
      }
      isLoading={isLoadingPatientProfile}
    >
      <FormProvider {...formMethods}>
        <Box display="flex">
          <Box maxWidth={500} width="100%">
            <Stack direction="row" flexWrap="wrap">
              <Box width={FIRST_COL_WIDTH} my={1}>
                <Typography variant="subtitle1">
                  {t('patients:title')}
                </Typography>
                <Typography variant="body2" fontWeight="600">
                  {patientProfile?.title || '-'}
                </Typography>
              </Box>
              <Box width={FIRST_COL_WIDTH} my={1}>
                <Typography variant="subtitle1">
                  {t('patients:data.lastName')}
                </Typography>
                <Typography variant="body2" fontWeight="600">
                  {patientProfile?.lastName || '-'}
                </Typography>
              </Box>
              <Box width={FIRST_COL_WIDTH} my={1}>
                <Typography variant="subtitle1">
                  {t('patients:data.firstName')}
                </Typography>
                <Typography variant="body2" fontWeight="600">
                  {patientProfile?.firstName || '-'}
                </Typography>
              </Box>
            </Stack>
            <Stack direction="row" my={1} flexWrap="wrap">
              <Typography width={FIRST_COL_WIDTH} variant="subtitle1">
                {t('patients:taj')}
              </Typography>
              <Typography variant="body2" fontWeight="600">
                {patientProfile?.tajNumber}
              </Typography>
            </Stack>
            <Stack direction="row" my={1} flexWrap="wrap">
              <Typography width={FIRST_COL_WIDTH} variant="subtitle1">
                {t('patients:birthdate')}
              </Typography>
              <Typography variant="body2" fontWeight="600">
                {t('common:dateFormatted', {
                  date: patientProfile?.birthDate,
                })}
              </Typography>
            </Stack>

            <Stack direction="row" my={1} flexWrap="wrap">
              <Typography width={FIRST_COL_WIDTH} variant="subtitle1">
                {t('patients:birthplace')}*
              </Typography>
              <Stack flexGrow={1}>
                <TextField
                  {...register('birthPlace', {
                    required,
                  })}
                  size="small"
                  fullWidth
                  error={!!errors.birthPlace}
                  helperText={errors.birthPlace?.message}
                  onBlur={() => trigger()}
                />
              </Stack>
            </Stack>

            <Stack direction="row" my={1} flexWrap="wrap">
              <Typography width={FIRST_COL_WIDTH} variant="subtitle1">
                {t('patients:mothersName')}*
              </Typography>
              <Stack flexGrow={1}>
                <BoxWithLabel label={t('patients:lastName')}>
                  <TextField
                    {...register('motherLastName', {
                      required,
                    })}
                    size="small"
                    fullWidth
                    error={!!errors.motherLastName}
                    helperText={errors.motherLastName?.message}
                    onBlur={() => trigger()}
                  />
                </BoxWithLabel>
                <BoxWithLabel label={t('patients:firstName')}>
                  <TextField
                    {...register('motherFirstName', {
                      required,
                    })}
                    size="small"
                    fullWidth
                    error={!!errors.motherFirstName}
                    helperText={errors.motherFirstName?.message}
                    onBlur={() => trigger()}
                  />
                </BoxWithLabel>
              </Stack>
            </Stack>

            <Stack direction="row" my={1} flexWrap="wrap">
              <Typography width={FIRST_COL_WIDTH} variant="subtitle1">
                {t('patients:gender')}
              </Typography>
              <Typography variant="body2" fontWeight="600">
                {t(`patients:genders.${patientProfile?.gender}`)}
              </Typography>
            </Stack>

            {!!patientProfile?.bloodType && (
              <Stack direction="row" flexWrap="wrap">
                <Typography width={FIRST_COL_WIDTH} variant="subtitle1">
                  {t('patients:bloodType')}
                </Typography>
                <Typography variant="body2" fontWeight="600">
                  {prettifyBloodType(patientProfile.bloodType)}
                </Typography>
              </Stack>
            )}

            <Divider />

            <Typography my={2} variant="subtitle1">
              {t('patients:data.info')}
            </Typography>

            <form>
              <Stack gap={1}>
                <Stack direction="row" flexWrap="wrap">
                  <Typography
                    width={FIRST_COL_WIDTH}
                    variant="subtitle1"
                    mt={1}
                    pr={2}
                  >
                    {t(`patients:phone`)}*
                  </Typography>
                  <Stack flexGrow={1}>
                    <TextField
                      {...register('phone', {
                        pattern: {
                          value: ValidPhonePattern,
                          message: t('messages:warnings.notPhone'),
                        },
                        required,
                      })}
                      size="small"
                      fullWidth
                      placeholder={'36 12 3456789'}
                      error={!!errors.phone}
                      helperText={errors.phone?.message}
                      onBlur={() => trigger()}
                    />
                  </Stack>
                </Stack>

                <Stack direction="row" flexWrap="wrap">
                  <Typography
                    width={FIRST_COL_WIDTH}
                    variant="subtitle1"
                    mt={1}
                    pr={2}
                  >
                    {t(`patients:address`)}
                  </Typography>

                  <Stack flexGrow={1}>
                    <Stack direction="row" gap={1} flexWrap="wrap">
                      <Box width={130}>
                        <BoxWithLabel
                          label={t(`patients:addressPostalCode`)}
                          size="small"
                        >
                          <TextField
                            {...register('addressPostalCode', {
                              pattern: {
                                value: /[0-9]{4}/,
                                message: t('messages:warnings.notPostalCode'),
                              },
                              maxLength: {
                                value: 4,
                                message: t('messages:warnings.notPostalCode'),
                              },
                            })}
                            size="small"
                            error={!!errors.addressPostalCode}
                            helperText={errors.addressPostalCode?.message}
                            onBlur={() => trigger()}
                          />
                        </BoxWithLabel>
                      </Box>

                      {!isCitiesLoading &&
                        cityOptions.includes(watch('addressCity') ?? '') && (
                          <BoxWithLabel
                            label={t(`patients:addressCity`)}
                            size="small"
                          >
                            <Controller
                              name="addressCity"
                              control={control}
                              render={({ field }) => (
                                <Select {...field} size="small" fullWidth>
                                  {cityOptions.map(
                                    (city: string, i: number) => (
                                      <MenuItem key={i} value={city}>
                                        {city}
                                      </MenuItem>
                                    )
                                  )}
                                </Select>
                              )}
                            />
                          </BoxWithLabel>
                        )}
                    </Stack>

                    <BoxWithLabel
                      label={t(`patients:addressStreet`)}
                      size="small"
                    >
                      <TextField
                        {...register('addressStreet')}
                        size="small"
                        fullWidth
                      />
                    </BoxWithLabel>
                  </Stack>
                </Stack>

                <Stack direction="row" flexWrap="wrap">
                  <Typography
                    width={FIRST_COL_WIDTH}
                    variant="subtitle1"
                    mt={1}
                    pr={2}
                  >
                    {t(`patients:body`)}
                  </Typography>

                  <Stack direction="row" gap={1} flexGrow={1} flexWrap="wrap">
                    <Box width={130}>
                      <BoxWithLabel label={t(`patients:height`)} size="small">
                        <MeasurementFieldController
                          name="height"
                          size="small"
                        />
                      </BoxWithLabel>
                    </Box>
                    <BoxWithLabel label={t(`patients:weight`)} size="small">
                      <MeasurementFieldController name="weight" size="small" />
                    </BoxWithLabel>
                  </Stack>
                </Stack>

                <Stack direction="row" flexWrap="wrap">
                  <Typography
                    width={FIRST_COL_WIDTH}
                    variant="subtitle1"
                    mt={1}
                    pr={2}
                  >
                    {t(`patients:medicine`)}*
                  </Typography>
                  <Stack flexGrow={1}>
                    <BoxWithLabel
                      label={t(`patients:takenMedicines`)}
                      size="small"
                    >
                      <TextField
                        {...register('takenMedicines', { required })}
                        size="small"
                        onBlur={() => trigger()}
                        fullWidth
                        multiline
                        error={!!errors.takenMedicines}
                        helperText={errors.takenMedicines?.message}
                      />
                    </BoxWithLabel>
                    <BoxWithLabel
                      label={t(`patients:medicineIntolerance`)}
                      size="small"
                    >
                      <TextField
                        {...register('medicineIntolerance', { required })}
                        size="small"
                        onBlur={() => trigger()}
                        fullWidth
                        multiline
                        error={!!errors.medicineIntolerance}
                        helperText={errors.medicineIntolerance?.message}
                      />
                    </BoxWithLabel>
                  </Stack>
                </Stack>
              </Stack>
            </form>
          </Box>
        </Box>
      </FormProvider>
    </ScrollablePanel>
  )
}

export { PatientProfile }
